import ReactECharts from 'echarts-for-react';
import type { MeasurementMarkerDto, SessionKindGroupName } from 'types/models/SessionsPageData';
import { AXIS_COLOR, GRID_LINE_COLOR, darkTooltip } from 'utilities/chartUtils';
import { t } from 'utilities/TranslationUtils';
import { KIND_FILTER } from './playerPerformanceUtils';

interface PlayerPerformanceActivityLast30DaysProps {
  measurements: MeasurementMarkerDto[];
}

function formatDateLabel(date: Date): string {
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

interface DaySeriesEntry {
  label: string;
  value: number;
  kindCounts: Record<SessionKindGroupName, number>;
}

function emptyKindCounts(): Record<SessionKindGroupName, number> {
  return {
    FreeKick: 0,
    Penalty: 0,
    Corner: 0,
    Shooting: 0,
    Goalkeeper: 0,
    ThrowIn: 0,
  };
}

export function buildLast30DaysSeries(measurements: MeasurementMarkerDto[]): DaySeriesEntry[] {
  const byDate = new Map<string, { value: number; kindCounts: Record<SessionKindGroupName, number> }>();
  for (const measurement of measurements) {
    const date = measurement.capturedAtUtc.slice(0, 10);
    const entry = byDate.get(date) ?? { value: 0, kindCounts: emptyKindCounts() };
    entry.value += 1;
    entry.kindCounts[measurement.kind] += 1;
    byDate.set(date, entry);
  }

  // Anchor the 30-day window to the player's most recent activity, not to today: the rightmost bar is
  // always the last session and the window spans the 30 days leading up to it. (A player whose last
  // session was weeks ago would otherwise show an all-empty "last 30 days".)
  const anchor = new Date();
  anchor.setHours(0, 0, 0, 0);
  if (byDate.size > 0) {
    const latestKey = Array.from(byDate.keys()).reduce((max, key) => (key > max ? key : max));
    const [year, month, dayOfMonth] = latestKey.split('-').map(Number);
    if (year && month && dayOfMonth) anchor.setFullYear(year, month - 1, dayOfMonth);
  }

  const rows: DaySeriesEntry[] = [];

  for (let index = 29; index >= 0; index -= 1) {
    const day = new Date(anchor);
    day.setDate(anchor.getDate() - index);
    const key = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
    const dayEntry = byDate.get(key);
    rows.push({
      label: formatDateLabel(day),
      value: dayEntry?.value ?? 0,
      kindCounts: dayEntry?.kindCounts ?? emptyKindCounts(),
    });
  }

  return rows;
}

const TOOLTIP_KIND_ORDER: SessionKindGroupName[] = ['FreeKick', 'Penalty', 'Corner', 'Shooting', 'Goalkeeper', 'ThrowIn'];

const PlayerPerformanceActivityLast30Days: React.FunctionComponent<PlayerPerformanceActivityLast30DaysProps> = ({ measurements }) => {
  const series = buildLast30DaysSeries(measurements);
  const maxValue = Math.max(...series.map((entry) => entry.value), 1);

  const option = {
    tooltip: {
      ...darkTooltip('axis'),
      formatter: (params: Array<{ axisValueLabel: string; value: number; dataIndex: number }>) => {
        const point = params[0];
        if (!point) return '';

        const day = series[point.dataIndex];
        const kindRows = TOOLTIP_KIND_ORDER
          .map((kind) => {
            const count = day.kindCounts[kind];
            if (count === 0) return null;
            return `${t(KIND_FILTER[kind].labelKey)}: ${count}`;
          })
          .filter((row): row is string => row != null);

        return [
          `<b>${point.axisValueLabel}</b>`,
          `${point.value} ${t('playerPerformance.activityMeasurementsLabel')}`,
          ...kindRows,
        ].join('<br/>');
      },
    },
    grid: { left: 38, right: 12, top: 12, bottom: 36, containLabel: false },
    xAxis: {
      type: 'category' as const,
      data: series.map((entry) => entry.label),
      axisLabel: {
        fontSize: 10,
        color: AXIS_COLOR,
        interval: 4,
      },
      axisLine: { lineStyle: { color: GRID_LINE_COLOR } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value' as const,
      min: 0,
      max: Math.max(2, Math.ceil(maxValue * 1.2)),
      minInterval: 1,
      axisLabel: {
        fontSize: 10,
        color: AXIS_COLOR,
      },
      splitLine: { lineStyle: { color: GRID_LINE_COLOR } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [{
      type: 'bar' as const,
      data: series.map((entry) => entry.value),
      barMaxWidth: 16,
      itemStyle: {
        color: '#EC691A',
        borderRadius: [4, 4, 0, 0] as [number, number, number, number],
      },
      emphasis: { itemStyle: { color: '#D95B10' } },
    }],
  };

  return <ReactECharts option={option} style={{ height: 180 }} notMerge />;
};

export default PlayerPerformanceActivityLast30Days;